'use client'

import { Button, Image, Spacer, Text } from '@vinted/web-ui'

import {
  businessAccountFromUserDto,
  BusinessAccountStatus,
  clickEvent,
} from '@marketplace-web/business-accounts/business-accounts-data'
import { navigateToPage } from '@marketplace-web/shared/browser-util'
import { useCookie } from '@marketplace-web/environment/cookies-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { findThumbnail } from '@marketplace-web/shared/utils'
import { LanguageSelector } from '@marketplace-web/language/language-feature'
import { logoutUser } from '@marketplace-web/session-management/session-management-data'

import { BUSINESS_REGISTER_PATH, ROUTES } from '../../constants/routes'
import SignUpButton from '../SignUpButton/SignUpButton'

type Props = {
  showRightButtons?: boolean
}

const ProNavBar = ({ showRightButtons }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate()
  const asset = useAsset('assets/web-logo/default')
  const noPhotoAsset = useAsset('assets/no-photo')
  const cookies = useCookie()
  const { user } = useSession()

  const isLoggedIn = !!user
  const userPhoto = user?.photo
  const businessAccount = businessAccountFromUserDto(user)

  const handleLogout = async () => {
    const response = await logoutUser({ cookies })

    if (response) navigateToPage(ROUTES.BASE)
  }

  const handleConvertClick = () => {
    track(
      clickEvent({
        target: 'convert_to_business',
        screen: 'business_homepage',
      }),
    )
  }

  const renderLogo = () => (
    <a
      className="u-flexbox u-align-items-center u-ui-padding-left-small business-accounts-navbar__logo"
      href={ROUTES.BASE}
    >
      <Image src={asset('logo.svg')} />
      <span className="business-accounts-navbar__logo-suffix">
        <Text as="span">{translate('business.title')}</Text>
      </span>
    </a>
  )

  const renderSignupButton = () => {
    if (!showRightButtons) return null

    return (
      <SignUpButton size={Button.Size.Small} inline text={translate('business.navbar.signup')} />
    )
  }

  const renderConvertUser = () => {
    if (!showRightButtons) return null

    return (
      <Button
        size={Button.Size.Small}
        styling={Button.Styling.Filled}
        inline
        text={translate('business.navbar.convert')}
        url={BUSINESS_REGISTER_PATH}
        onClick={handleConvertClick}
      />
    )
  }

  const renderLogoutButton = () => {
    if (!isLoggedIn) return null
    if (businessAccount?.status !== BusinessAccountStatus.WalletConversionInProgress) return null

    return (
      <Button
        size={Button.Size.Small}
        styling={Button.Styling.Flat}
        theme="amplified"
        inline
        text={translate('business.navbar.logout')}
        onClick={handleLogout}
      />
    )
  }

  const renderUserImage = () => {
    if (!isLoggedIn) return null

    return (
      <>
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Large} />
        <Image
          role="img"
          src={findThumbnail(userPhoto) || noPhotoAsset(EMPTY_USER_IMAGE_NAME)}
          size={Image.Size.Regular}
          styling={Image.Styling.Circle}
        />
      </>
    )
  }

  const renderLanguageSelector = () => (
    <div className="u-position-relative">
      <LanguageSelector isStandalone />
    </div>
  )

  const renderRightButtons = () => (
    <>
      {renderLogoutButton()}
      <div className="u-desktops-only">
        {!isLoggedIn && renderSignupButton()}
        {isLoggedIn && !businessAccount && renderConvertUser()}
        {renderUserImage()}
      </div>
      {renderLanguageSelector()}
    </>
  )

  return (
    <div className="business-accounts-navbar action-header">
      <div className="business-accounts-navbar__container">
        <div>{renderLogo()}</div>
        <div className="business-accounts-navbar__right-buttons">{renderRightButtons()}</div>
      </div>
    </div>
  )
}

export default ProNavBar
